<template>
  <v-card
    class="transparent"
  >
    <v-banner
      icon="mdi-transit-connection-variant"
      single-line
    >
      <h2>Related Docket Entries</h2>

      <template #actions>
        <v-btn
          icon
          outlined
          @click="_ => openDocketEntrySelector()"
        >
          <v-icon>mdi-sign-direction-plus</v-icon>
        </v-btn>
      </template>
    </v-banner>

    <v-data-table
      :headers="dataTableHeaders"
      hide-default-footer
      :items="relationListItems"
      :items-per-page="-1"
      class="transparent"
      v-bind="{ dark }"
    >
      <template
        #item.docketEntryId="{ item }"
      >
        <div
          class="text-no-wrap"
        >
          <v-icon
            v-if="item.isChild"
            small
          >
            mdi-arrow-right-thin
          </v-icon>
          <v-icon
            v-else
            small
          >
            mdi-arrow-left-thin
          </v-icon>
          <a
            :href="item.url"
            target="_blank"
          >
            {{ item.docketEntryId }}
          </a>
          <v-icon
            v-if="item.isProtected"
            right
            x-small
          >
            mdi-lock
          </v-icon>
        </div>
      </template>

      <template
        #item.actions="{ item }"
      >
        <div
          class="d-flex justify-right"
        >
          <v-spacer />

          <v-btn
            v-if="!item.isProtected"
            icon
            small
            @click="() => deleteRelation(item.docketEntryId)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>

          <v-icon
            v-else
          >
            mdi-lock
          </v-icon>
        </div>
      </template>
    </v-data-table>

    <v-dialog
      v-if="isDocketEntrySelectorVisible"
      v-model="isDocketEntrySelectorVisible"
      max-width="600"
      persistent
    >
      <v-card>
        <v-card-title>
          Relate with Docket Entry
        </v-card-title>

        <v-container>
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="selectedDocketEntryId"
                filled
                label="Docket Entry"
                :items="relatableDocketEntries"
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>
        </v-container>

        <v-divider />

        <v-card-actions>
          <v-btn
            color="secondary"
            text
            @click="_ => closeDocketEntrySelector()"
          >
            Cancel
          </v-btn>

          <v-spacer />

          <v-btn
            color="primary"
            :disabled="!selectedDocketEntryId"
            @click="_ => createRelation()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'

import { alertHandler } from '@/alertHandler'
import { dateDisplayFormat } from '@/utilities'
import { rules } from '@/utilities/validation-rules'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'

export default {
  name: 'DocketEntryEditorRelations',

  props: {
    dark: {
      type: Boolean,
      default: undefined,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      isDocketEntrySelectorVisible: false,
      rules,
      selectedDocketEntryId: undefined,
    }
  },

  computed: {
    dataTableHeaders () {
      const headers = [
        {
          sortable: false,
          text: 'Docket #',
          value: 'docketEntryId',
          width: 0,
        },
        {
          sortable: true,
          text: 'Relation',
          value: 'relationType',
          width: 0,
        },
        {
          sortable: false,
          text: 'Description',
          value: 'description',
        },
      ]

      headers.push({
        sortable: false,
        text: '',
        value: 'actions',
      })

      return headers
    },

    docketEntry () {
      return this.editedDocketEntry
    },

    docketEntryId () {
      return this.docketEntry.id
    },

    relatableDocketEntries () {
      return Object.keys(this.docketEntriesInfo).
        reduce((p, k) => {
          const docket = this.docketEntriesInfo[k]
          if (docket.id === this.docketEntryId ||
              this.relatedDocketEntryIds.includes(docket.id) ||
              docket._destroy ||
              docket.deleted
          ) return p

          p.push({
            position: Date.parse(docket.action_date),
            text: `${docket.action_type} #${docket.id} - ${docket.title} (${dateDisplayFormat(docket.action_date)})`,
            value: Number(docket.id),
          })
          return p
        }, []).
        sort((a, b) => b.position - a.position)
    },

    relatedDocketEntryIds () {
      const parents = (this.docketEntry.parent_docket_entry_relations || []).
        reduce((res, rel) => {
          if (!rel._destroy) res.push(rel.parent_docket_entry_id)
          return res
        }, [])

      const children = (this.docketEntry.child_docket_entry_relations || []).
        reduce((res, rel) => {
          if (!rel._destroy) res.push(rel.child_docket_entry_id)
          return res
        }, [])

      return parents.concat(children)
    },

    relationListItems () {
      const parents = (this.docketEntry.parent_docket_entry_relations || []).reduce((res, rel) => {
        if (rel._destroy) return res

        const de = this.docketEntriesInfo[rel.parent_docket_entry_id]

        res.push({
          description: de?.title || 'Outdated cache',
          docketEntryId: rel.parent_docket_entry_id,
          isChild: false,
          isProtected: rel.isProtected,
          relationType: 'Child',
          url: de?.url,
        })

        return res
      }, [])

      const children = (this.docketEntry.child_docket_entry_relations || []).reduce((res, rel) => {
        if (rel._destroy) return res

        const de = this.docketEntriesInfo[rel.child_docket_entry_id]

        res.push({
          description: de?.title || 'Outdated cache',
          docketEntryId: rel.child_docket_entry_id,
          isChild: true,
          isProtected: rel.isProtected,
          relationType: 'Child',
          url: de?.url,
        })

        return res
      }, [])

      return parents.concat(children)
    },

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'editedDocketEntry',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'docketEntriesInfo',
    ]),
  },

  methods: {
    closeDocketEntrySelector () {
      this.isDocketEntrySelectorVisible = false
      this.selectedDocketEntryId = undefined
    },

    createRelation () {
      if (!this.docketEntry.child_docket_entry_relations) {
        Vue.set(this.docketEntry, 'child_docket_entry_relations', [])
      }

      const rel = this.docketEntry.child_docket_entry_relations.find(r => r.child_docket_entry_id === this.selectedDocketEntryId)

      if (!rel) {
        this.docketEntry.child_docket_entry_relations.push({
          child_docket_entry_id: this.selectedDocketEntryId,
          is_protected: false,
          parent_docket_entry_id: this.docketEntry.id,
        })
      } else if (rel._destroy) {
        delete rel._destroy
      }

      this.closeDocketEntrySelector()
    },

    deleteRelation (docketEntryId) {
      let relations = this.docketEntry.child_docket_entry_relations || []
      let idx = relations.findIndex(r => r.child_docket_entry_id === docketEntryId)

      if (idx < 0) {
        relations = this.docketEntry.parent_docket_entry_relations || []
        idx = relations.findIndex(r => r.parent_docket_entry_id === docketEntryId)
      }

      if (relations[idx].id) {
        Vue.set(relations[idx], '_destroy', true)
      } else {
        relations.splice(idx, 1)
      }
    },

    openDocketEntrySelector () {
      this.selectedDocketEntryId = undefined
      this.isDocketEntrySelectorVisible = true
    },
  },
}
</script>
